import { Injectable } from '@angular/core';
import { PermissionModel, SetInitialStateFacade } from '../../../../common/models';
import { CatalogSearchCriteriaFacade, CatalogSearchCriteriaType } from '../../catalog-search-criteria';
import { AutomotiveAftermarketSearchCriteria, AutomotiveAftermarketListRequestModel } from './models';
import { Store } from '@ngxs/store';
import {
  AutomotiveAftermarketListInitialState,
  AutomotiveAftermarketListLoad,
} from './automotive-aftermarket-list.actions';
import { combineLatest, Observable } from 'rxjs';
import {
  AutomotiveAftermarketCardItem,
  AutomotiveAftermarketListState,
  AutomotiveAftermarketListStateModel,
} from './automotive-aftermarket-list.state';
import { MonitoringFilterNameDictionary } from '../../../models';
import { AutomotiveAftermarketFiltersDictionaryFacade } from './filters-dictionary/automotive-aftermarket-filters-dictionary.facade';
import { AuthFacade } from '../../auth';
import { Permission } from '../../../../common/constants';
import { convertTileType, transformPermissionArrayToObject } from '../../../../common/utils';
import { filter, switchMap, take } from 'rxjs/operators';
import { timer } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AutomotiveAftermarketListFacade implements SetInitialStateFacade {
  listDto$: Observable<AutomotiveAftermarketListStateModel> = this.store.select(
    AutomotiveAftermarketListState.getState,
  );
  items$: Observable<AutomotiveAftermarketCardItem[]> = this.store.select(AutomotiveAftermarketListState.getItems);
  loaded$: Observable<boolean> = this.store.select(AutomotiveAftermarketListState.getLoaded);
  isMoreButtonVisible$: Observable<boolean> = this.store.select(AutomotiveAftermarketListState.isMoreButtonVisible);
  isAnyFilterApplied$: Observable<boolean> = this.store.select(AutomotiveAftermarketListState.isAnyFilterApplied);
  isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff$: Observable<boolean> = this.store.select(
    AutomotiveAftermarketListState.isAllAutomotiveAftermarketTileTypeFiltersSwitchedOff,
  );

  constructor(
    private catalogSearchCriteriaFacade: CatalogSearchCriteriaFacade,
    private store: Store,
    private automotiveAftermarketFiltersDictionaryFacade: AutomotiveAftermarketFiltersDictionaryFacade,
    private authFacade: AuthFacade,
  ) {}

  loadAll(): void {
    timer(0)
      .pipe(
        switchMap(() =>
          combineLatest([
            this.catalogSearchCriteriaFacade.getSearchCriteriaByCatalogType(
              CatalogSearchCriteriaType.AutomotiveAftermarket,
            ),
            this.automotiveAftermarketFiltersDictionaryFacade.items$.pipe(
              filter((items: MonitoringFilterNameDictionary[]) => !!items.length),
              take(1),
            ),
            this.authFacade.permissions$.pipe(take(1)),
          ]),
        ),
      )
      .subscribe(
        ([{ viewMode: _viewMode, tileType, page, size, ...rest }, filtersDictionary, permissions]: [
          AutomotiveAftermarketSearchCriteria,
          MonitoringFilterNameDictionary[],
          PermissionModel[],
        ]) => {
          const payload: Partial<AutomotiveAftermarketListRequestModel> = { page, size };
          filtersDictionary.forEach((item: MonitoringFilterNameDictionary) => {
            payload[item] = rest[item];
          });

          const tileTypePermissions =
            transformPermissionArrayToObject(permissions)[Permission.TrackAndTrace][Permission.AutomotiveAftermarket][
              Permission.TileType
            ];

          this.store.dispatch(
            new AutomotiveAftermarketListLoad({
              ...payload,
              tileType: convertTileType(tileType, tileTypePermissions),
            }),
          );
        },
      );
  }

  setInitialState(): void {
    this.store.dispatch(new AutomotiveAftermarketListInitialState());
  }
}
